import { SVGProps } from "react"
import styled from "styled-components"

import { OrderIcon } from "@minuthq/meatball-ui-icons"
import {
  ButtonStyleReset,
  cardBoxShadow,
  colors,
  ListBox,
  Popover,
  Text,
} from "@minuthq/meatball-ui-react"

import {
  OrganizationAvatar,
  RoundProfilePicture,
} from "src/components/Account/Avatars"
import { getProfilePopoverRoot } from "src/components/AppLayout/navUtils"
import { ErrorBoundary } from "src/components/ErrorBoundary/ErrorBoundary"
import { useAppData } from "src/context/useAppData"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchOrganizations } from "src/data/organizations/queries/organizationQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useLogout } from "src/hooks/useLogout"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { TPartialLocation } from "src/router/routeTypes"
import { useRouter } from "src/router/useRouter"
import { MButton } from "src/ui/Button/MButton"
import { Divider } from "src/ui/Divider/Divider"
import NotificationsIcon from "src/ui/icons/bell.svg"
import CardIcon from "src/ui/icons/card.svg"
import Checkmark from "src/ui/icons/checkmark-base.svg"
import CreateOrgIcon from "src/ui/icons/create-org.svg"
import Chevron from "src/ui/icons/expand-down.svg"
import JoinOrgIcon from "src/ui/icons/join-org.svg"
import OrganizationIcon from "src/ui/icons/organization.svg"
import ProfileIcon from "src/ui/icons/profile.svg"
import WarningIcon from "src/ui/icons/warning.svg"
import { SVGElement } from "src/utils/tsUtil"

import {
  ellipsis,
  Fullname,
  ProfileInfo,
  ProfileInfoCenter,
  ProfileInfoCenterTitle,
  ProfileListItem,
} from "./sharedStyles"

const SIXTY_SECONDS = 60 * 1000
const MAX_ORGS = 5

interface TAccountPages {
  url: TPartialLocation
  label: string
  suffix?: string | React.FC<SVGProps<SVGSVGElement>>
  icon: SVGElement
  hidden?: boolean
}

/**
 * NEED TO BE ABLE TO CLOSE WHEN CLICKING INSIDE THE POPOVER CONTENT
 *
 * NEED TO BE ABLE TO IMPORT LIST ITEM COMPONENT FROM MBUI
 */
export function ProfileButton({
  showLabel = true,
  clickCb = () => {},
}: {
  showLabel?: boolean
  clickCb?: () => void
}) {
  const { setActiveOrgId } = useAppData()
  const { org, orgAccessLogic } = useOrganization()

  const isOrgOwner = orgAccessLogic.hasOwnerAccess

  const fetchOrganizations = useFetchOrganizations({
    params: { limit: MAX_ORGS },
    options: { staleTime: SIXTY_SECONDS },
  })

  const hasMaxOrgs =
    (fetchOrganizations.data?.paging.total_count || 0) > MAX_ORGS

  const orgNavList = hasMaxOrgs
    ? [org]
    : (fetchOrganizations.data?.organizations ?? [])

  const { t, langKeys } = useTranslate()

  const user = useGetUser()
  const { navigate } = useRouter()

  const { logout } = useLogout()

  const accountPages: Readonly<TAccountPages[]> = [
    {
      url: Routes.AccountGeneral.location(),
      label: t(langKeys.account),
      suffix: WarningIcon,
      icon: ProfileIcon,
    },
    {
      url: Routes.AccountBilling.location(),
      label: t(langKeys.settings_billing),
      icon: CardIcon,
      hidden: !isOrgOwner,
    },
    {
      url: Routes.AccountOrders.location(),
      label: t(langKeys.ecommerce_order_plural),
      icon: OrderIcon,
      hidden: !isOrgOwner,
    },
    {
      url: Routes.AccountNotifications.location(),
      label: t(langKeys.notifications),
      icon: NotificationsIcon,
    },
  ] as const

  return (
    <ErrorBoundary mini>
      <Popover
        trigger={
          <StyledMButton
            aria-controls="profile-menu"
            aria-haspopup="true"
            variant="minimal"
            fullWidth={showLabel}
          >
            <ProfileInfo>
              <OrganizationAvatar orgName={org.name} />
              {showLabel && (
                <ProfileInfoCenter>
                  <ProfileInfoCenterTitle>
                    <SubtitleSEllipsis>{org.name}</SubtitleSEllipsis>
                    <Chevron fill={colors.textSecondary} width={10} />
                  </ProfileInfoCenterTitle>

                  <Fullname>{user.fullname}</Fullname>
                </ProfileInfoCenter>
              )}
            </ProfileInfo>
          </StyledMButton>
        }
        placement="bottom-start"
        offset={{
          mainAxis: 10,
          crossAxis: 16,
        }}
        shift={{
          mainAxis: true,
          crossAxis: true,
          padding: 10,
        }}
        root={getProfilePopoverRoot()}
      >
        {({ closeMenu }) => (
          <PopoverContent onClick={closeMenu}>
            <ProfileListItem>
              <div>
                <RoundProfilePicture />
              </div>
              <ProfileInfoCenter>
                <Text variant="body">{user.fullname}</Text>
                <Text variant="bodyS" color="secondary">
                  {user.email}
                </Text>
              </ProfileInfoCenter>
            </ProfileListItem>

            <Divider />

            <ListBox
              items={[
                ...orgNavList.map((o) => ({
                  id: o.id,
                  prefix: <OrganizationAvatar orgName={o.name} size="small" />,
                  content: o.name,
                  suffix: o.id === org.id ? Checkmark : undefined,
                  onClick: () => {
                    setActiveOrgId(o.id)
                    navigate(Routes.Dashboard.location(), {
                      orgIdOverride: o.id,
                    })
                  },
                })),
                ...(hasMaxOrgs
                  ? [
                      {
                        id: "view-all-organizations",
                        prefix: <OrganizationIcon width="24" height="24" />,
                        content: t(
                          langKeys.organizations_view_all_organizations
                        ),
                        onClick: () => {
                          navigate(Routes.Organizations.location())
                        },
                      },
                    ]
                  : []),
                {
                  id: "create-organization",
                  prefix: <CreateOrgIcon width="24" height="24" />,
                  content: t(langKeys.organization_create),
                  onClick: () => {
                    navigate(Routes.OrgCreate.location())
                  },
                },
                {
                  id: "join-organization",
                  prefix: <JoinOrgIcon width="24" height="24" />,
                  content: t(langKeys.join_organization),
                  onClick: () => {
                    navigate(Routes.OnboardingJoinOrganization.location())
                  },
                },
              ]}
            />

            <Divider />

            <ListBox
              items={accountPages
                .filter((acc) => !acc.hidden)
                .map((page) => ({
                  id: page.url.pathname,
                  prefix: <page.icon width="24" height="24" />,
                  content: page.label,
                  suffix: page.suffix,
                  onClick: () => {
                    clickCb()
                    navigate(page.url)
                  },
                }))}
            />

            <Divider />

            <ListBox
              items={[
                {
                  id: "logout",
                  content: t(langKeys.sign_out),
                  onClick: () => {
                    logout({
                      reason: "User clicked logout in side nav",
                      userInitiated: true,
                    })
                  },
                },
              ]}
            />

            <ListItem
              onClick={() => {
                logout({
                  reason: "User clicked logout in side nav",
                  userInitiated: true,
                })
              }}
            ></ListItem>
          </PopoverContent>
        )}
      </Popover>
    </ErrorBoundary>
  )
}

const SubtitleSEllipsis = styled(Text).attrs(() => ({
  color: "secondary",
  variant: "subtitleS",
}))`
  ${ellipsis};
`

const StyledMButton = styled(MButton)`
  text-decoration: none;
`

const PopoverContent = styled.div`
  background-color: ${colors.neutralLight};
  border-radius: 1rem;
  ${cardBoxShadow}
`

const ListItem = styled.button`
  ${ButtonStyleReset}
  display: block;
  width: 100%;
  border-radius: 0px;
  background: none;
  outline: none;
`
